import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import {
  ESegmentedToggleComponent,
  ESegmentedToggleOption,
} from '@shared/components/atoms/inputs/e-segmented-toggle/e-segmented-toggle.component';
import { TranslocoService } from '@ngneat/transloco';
import moment from 'moment';
import { PlatformChartRelationDto } from '@data/_generated-api/platform-api/models/platform-chart-relation-dto';
import { PlatformChartType } from '@data/_generated-api/platform-api/models/platform-chart-type';
import { HdChartDataCache } from '../../library/hd-chart-data-cache.service';
import { HdChartPanelViewMode } from '../hd-chart-panel-view-mode';
import { HdBirthWindowIndicatorComponent } from '../../birth-window/hd-birth-window-indicator.component';
import { HD_BIRTH_WINDOW_CHART_TYPES } from '../../birth-window/birth-window.models';
import { buildHdBirthWindowMomentPreset, hdBirthWindowOverlayOf } from '../../birth-window/birth-window-moment';
import {
  HdChartRelationsDialogComponent,
  HdChartRelationsTarget,
} from '../../library/hd-chart-relations-dialog.component';
import {
  hdRelationTypeIcon,
  hdRelationTypeLabel,
  hdRelationTypeOrder,
  isHdSystemRelationType,
} from '../../library/hd-chart-relation-metadata';
import { hdTypeLabel } from '../../library/hd-chart-property-labels';
import { findHdCycleMoment, mergeCycleConfiguration, mergeCyclePlanetaryActivations } from '../../library/hd-cycle-chart.utils';
import { HD_JUST_NOW_TRANSIT_BASE_COLOR, HD_JUST_NOW_TRANSIT_TEXT_COLOR } from '../../mandala/just-now-transit/hd-just-now-transit';
import { HdLovePanelService } from '../../love/hd-love-panel.service';
import { HD_LOVE_GATES } from '../../love/hd-love.metadata';
import { getHdActivatedGates } from '../hd-cycle-keynotes.builder';

/** Eén persoon die een liefdespoort activeert (connectiviteitspoorten-sectie). */
interface LoveConnectivityPersonVm {
  readonly name: string;
  readonly color: string;
}

/** Eén liefdespoort die minstens één van beide composite-charts activeert. */
interface LoveConnectivityRowVm {
  readonly gate: number;
  /** Basic love-keynote (de titel op de Love gates cheat sheet). */
  readonly keynote: string;
  readonly persons: readonly LoveConnectivityPersonVm[];
}

interface LoveConnectivityVm {
  readonly title: string;
  readonly hint: string;
  readonly rows: readonly LoveConnectivityRowVm[];
  readonly none: string;
}
import { HdLocksKeysPeriodId, hdLocksKeysPeriodForDate } from '../../mandala/locks-keys/hd-locks-keys-metadata';
import { HdLocksKeysChartSummaryComponent } from '../../mandala/locks-keys/hd-locks-keys-chart-summary.component';
import {
  HdLocksKeysRelationshipRowVm,
  buildHdLocksKeysRelationshipRows,
} from '../../mandala/locks-keys/hd-locks-keys-relationship';
import { HdContentLanguageService } from '../hd-content-language.service';
import { HdGateInfoService } from '../hd-gate-info.service';
import { HdPlanetInfoService } from '../hd-planet-info.service';
import { getHdPlanetDisplayConfig } from '../planet-grid/hd-planet-display-config';
import { HdChartSettingsStore } from '../chart-tabs/hd-chart-settings.store';
import { IChingLinePosition } from '../../../iching/core/iching.models';
import { HdIChingAccessService } from '../../../iching/hd-iching-access.service';
import { HdIChingBirthTarget, HdIChingPairTarget } from '../../../iching/panel/hd-iching-panel.models';
import { HdIChingPanelService } from '../../../iching/panel/hd-iching-panel.service';
import {
  formatHdFixedStarDistance,
  resolveHdFixedStars,
} from '../../fixed-stars/hd-fixed-stars.util';
import {
  HdFixedStarPanelContext,
  HdFixedStarsPanelService,
} from '../../fixed-stars/hd-fixed-stars-panel.service';
import { HdRelationInfoTopic } from '../relation-info/hd-relation-info.service';
import {
  deriveDefinition,
  deriveIncarnationCross,
  deriveLifeCross,
  derivePrimaryConditioning,
  formatHdIncarnationCrossValue,
  formatHdLifeCrossValue,
} from '../basic-properties/hd-chart-properties.util';
import { HdPrimaryConditioningValueComponent, HdPrimaryConditioningVm, buildHdPrimaryConditioningVm } from '../basic-properties/hd-primary-conditioning-value.component';
import {
  HdPresetBirthData,
  HdPresetBirthLocation,
  HdPresetBirthTime,
  HdPrimaryConditioningKind,
} from '../basic-properties/hd-chart-properties.types';
import { HdBridgeGateStyle } from '../hd-chart-controls-options';
import { HdChartHoverHighlightService } from '../hd-chart-hover-highlight.service';
import { HdBodygraphCenter, HdBodygraphChartPreset } from '../hd-bodygraph.models';
import { HdBodygraphRenderStateFactory } from '../hd-bodygraph-render-state.factory';
import { HdChartMatchScope } from '../chart-tabs/hd-chart-global-settings';
import {
  HdChannelListChannelInfoRequest,
  HdChannelListGateInfoRequest,
} from '../channel-list/hd-channel-list-item.component';
import { HdCompositeCalculateRequest, HdCompositeSourceRefV1 } from '../hd-composite-chart.models';
import { HdCycleCalculateRequest, HdCycleChartInfoV1, HdCyclePassageV1 } from '../hd-cycle-chart.models';
import { HdChartCyclesService } from '../../library/hd-chart-cycles.service';
import { HdAgeSuffixComponent } from './hd-age-suffix.component';
import { HdCompositeSectionsComponent } from './hd-composite-sections.component';
import { HdCycleKeynoteSectionsComponent } from './hd-cycle-keynote-sections.component';
import { HdCycleCenterBlockInfoRequest } from './hd-cycle-conditioned-center-block.component';
import {
  HdCycleDefinitionInfoRequest,
  HdCycleChironInfoRequest,
  HdCycleLifeCrossInfoRequest,
  HdCyclesSectionComponent,
} from './hd-cycles-section.component';
import { HdMomentsSectionComponent } from './hd-moments-section.component';
import { HdActivePaletteService } from '../hd-active-palette.service';

interface BirthVm {
  label: string;
  text: string;
  /** Huidige leeftijd in volle jaren (hd-age-suffix rendert "· 13 jaar"); maskeerbaar deel. */
  age: number | null;
  tooltip: string | null;
  showLabel: string;
  hideLabel: string;
  /**
   * Design-tijdstip (88° zonneboog vóór de geboorte) in de geboortetijdzone;
   * hoort bij het maskeerbare deel, zoals de leeftijd. Null wanneer het
   * document het veld (nog) niet draagt.
   */
  design: HdDesignTimeVm | null;
}

/** "Design tijdstip"-rij onder een geboorte- of cyclemoment-rij. */
interface HdDesignTimeVm {
  label: string;
  text: string;
  tooltip: string;
}

/** Eigenschappen-rij van een composite chart met per persoon een gekleurde waarde. */
interface CompositeInfoRowVm {
  readonly topic: HdRelationInfoTopic;
  readonly label: string;
  readonly infoLabel: string;
  /** inline = "A · B" naast elkaar; stacked = per persoon een eigen regel (lange kruis-waarden). */
  readonly layout: 'inline' | 'stacked';
  readonly values: readonly { text: string; color: string }[];
}

/** Eén Relaties-lijstrij: de hoogst-gerangschikte relatie + de overige typen van die persoon. */
interface RelationRowVm {
  readonly relation: PlatformChartRelationDto;
  readonly extraTypes: readonly string[];
}

/** Eén fixed-star-regel (de primaire of een extra) in de Fixed star-sectie. */
interface FixedStarRowVm {
  readonly side: number;
  readonly name: string;
  readonly distance: string;
  readonly label: string;
  readonly planetGlyph: string;
  readonly context: HdFixedStarPanelContext;
}

/** Eén periode-optie in de segmented pre/post-toggle van de relatie-sectie. */

interface LocksKeysRelationshipVm {
  readonly title: string;
  readonly hint: string;
  readonly periodLabel: string;
  /** Pre/post-2027-knoppen (gedeelde e-segmented-toggle). */
  readonly periodOptions: readonly ESegmentedToggleOption<HdLocksKeysPeriodId>[];
  readonly rows: readonly HdLocksKeysRelationshipRowVm[];
  readonly none: string;
  readonly moreInfo: string;
}

interface FixedStarsVm {
  readonly title: string;
  readonly available: boolean;
  readonly unknown: string;
  readonly primary: FixedStarRowVm | null;
  readonly extrasTitle: string;
  /** Leeg zolang de extras dicht staan: ze worden pas op aanvraag berekend. */
  readonly extras: readonly FixedStarRowVm[];
  readonly extrasEmpty: string;
  readonly extrasToggle: string;
  readonly infoTooltip: string;
  readonly extrasInfoTooltip: string;
  readonly generalInfoTooltip: string;
}

const P = 'humanDesign.properties';

/**
 * Cyclus-einddatum voorlopig verborgen (2026-09-10, op Stevens vraag): de
 * periodes dragen domeinnuances die eerst uitgewerkt en toegelicht moeten
 * worden — een solar return start bv. eigenlijk vanaf de design-datum terwijl
 * de echte cyclus pas op de geboortedatum valt, en ook de andere cycli kennen
 * zulke nuances; hard getoonde datums zouden misvattingen kunnen wekken. De
 * berekening ({@link HdAdvancedPropertiesComponent#cycleEndText} + de
 * passages-fallback) blijft staan voor die vervolgronde.
 */
const SHOW_CYCLE_END_DATE: boolean = false;

/**
 * Vast "Geavanceerd"-dockpaneel in de chart environment (tussen Basis en
 * Editor). Verzamelplek voor verdieping rond de actieve chart; start met twee
 * secties: Eigenschappen (de geboortegegevens, verhuisd uit het Basis-paneel)
 * en Relaties (fase 5-data; klik opent de andere chart in een nieuwe tab).
 */
@Component({
  selector: 'hd-advanced-properties',
  standalone: true,
  imports: [
    ESegmentedToggleComponent,
    HdPrimaryConditioningValueComponent,
    HdAgeSuffixComponent,
    HdChartRelationsDialogComponent,
    HdCompositeSectionsComponent,
    HdCyclesSectionComponent,
    HdCycleKeynoteSectionsComponent,
    HdMomentsSectionComponent,
    HdLocksKeysChartSummaryComponent,
    HdBirthWindowIndicatorComponent,
  ],
  templateUrl: './hd-advanced-properties.component.html',
  styleUrl: './hd-advanced-properties.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdAdvancedPropertiesComponent {
  /** Ruwe `preset.birthData` van de actieve chart; `null` voor presets zonder geboortegegevens. */
  readonly birthData = input<Record<string, unknown> | null>(null);
  /** Library-chart-id van de actieve tab; null = niet opgeslagen → relaties niet beschikbaar. */
  readonly savedChartId = input<string | null>(null);
  /** Naam van de actieve chart (voor de titel van de relatie-dialog). */
  readonly chartName = input('');

  /** Charttype van de actieve tab (bepaalt of de Cycles-sectie toont). */
  readonly chartType = input<PlatformChartType>(PlatformChartType.Standard);
  /** Volledige preset van de actieve tab (Cycles-sectie: geboortejaar, cycle-blok). */
  readonly preset = input<HdBodygraphChartPreset | null>(null);
  /** Scope van de "gelijkaardige charts"-matchlijsten (globale setting). */
  readonly matchScope = input<HdChartMatchScope>('library');
  /** Weergavestijl van brugpoort-nummers (zelfde globale setting als het Basis-paneel). */
  readonly bridgeGateStyle = input<HdBridgeGateStyle>('badge');
  /**
   * Weergavestand (spec 2026-10-20-chart-share §4.5): shared → Relaties en Momenten
   * tonen niets; guest → bovendien meldt elke klik die iets opent "account nodig".
   */
  readonly viewMode = input<HdChartPanelViewMode>('owner');

  /** Gaststand: een klik die normaal een paneel opent; de host meldt dat een account nodig is. */
  readonly accountRequired = output<void>();
  /** Klik op een relatie: open die chart (de page laadt hem in een nieuwe tab). */
  readonly chartOpenRequested = output<string>();
  /** Openen vanuit de sectie-kalender: nieuwe tab mét het Notities-paneel (spec levenstijdlijn §2.9). */
  readonly chartOpenWithNotesRequested = output<string>();
  /** Tijdlijn-knop in de Momenten-sectie → de levenstijdlijn-pagina (stap 5). */
  readonly timelineRequested = output<string>();
  /** Brein-knop in de Momenten-sectie → het paneel "Mijn mind zegt" (spec 2026-10-22-mind-logboek §5). */
  readonly mindLogRequested = output<string>();
  /** Cycles-sectie → cycle chart berekenen (de page voert de flow uit). */
  readonly cycleCalculateRequested = output<HdCycleCalculateRequest>();
  /** Cycles-sectie → "Just Now Transit": het huidige moment als momentchart over deze chart. */
  readonly cycleJustNowTransitRequested = output<void>();
  /** Cycles-sectie op een cycle chart → herberekenen (verouderd). */
  readonly cycleRecalculateRequested = output<void>();
  /** Cycles-sectie op een cycle chart → nodale-omgevingen-paneel met het levenskruis van de cyclus. */
  readonly cycleLifeCrossInfoRequested = output<HdCycleLifeCrossInfoRequest>();
  /** Cycles-sectie (Chiron return) → poortinfo met een Chiron-positie uit het drieluik. */
  readonly cycleChironInfoRequested = output<HdCycleChironInfoRequest>();
  /** Info-icoon op de definitie-rij (natal + cyclus) van een cycle chart. */
  readonly cycleDefinitionInfoRequested = output<HdCycleDefinitionInfoRequest>();
  /** Relatie-rij → composiet berekenen met die persoon (de page voert de flow uit). */
  readonly compositeCalculateRequested = output<HdCompositeCalculateRequest>();
  /** Info-icoon bij het composiet-thema → docked info-paneel openen (de page). */
  readonly compositeThemeInfoRequested = output<{
    theme: string;
    themeKey: string;
    origin: { x: number; y: number };
  }>();
  /** Info-icoon/link bij een relatieboek-onderwerp → relation-info-paneel openen (de page). */
  readonly relationInfoRequested = output<{ topic: HdRelationInfoTopic; origin: { x: number; y: number } }>();
  /** Keynote-secties → gate-info-paneel openen (zoals een klik op de poort in de chart). */
  readonly gateInfoRequested = output<HdChannelListGateInfoRequest>();
  /** Keynote-secties → kanaal-info-paneel openen (zoals een klik op het kanaal in de chart). */
  readonly channelInfoRequested = output<HdChannelListChannelInfoRequest>();
  /** Keynote-secties → center-info-paneel openen (klik op een "Niet zelf center thema"-blokje). */
  readonly centerInfoRequested = output<HdCycleCenterBlockInfoRequest>();
  /**
   * "Bekijk Locks & Keys"-knop (composite-relatie-sectie) en "Toon de volledige
   * modus"-knop (Locks & Keys-basisinfo) → de page opent de L&K-modus.
   */
  readonly locksKeysRequested = output<void>();
  /** Klik op de betrouwbaarheidschip bij de geboortegegevens: open het geboortevenster (spec 2026-10-16 §3.1). */
  readonly birthWindowRequested = output<void>();
  /** Info-icoon op de composiet-conditioneringsrij → conditionering-info-paneel (de page). */
  readonly conditioningInfoRequested = output<{ origin: { x: number; y: number } }>();

  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly centerColors = inject(HdActivePaletteService);
  private readonly dataCache = inject(HdChartDataCache);
  private readonly renderStateFactory = inject(HdBodygraphRenderStateFactory);
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly cyclesService = inject(HdChartCyclesService);
  private readonly planetInfo = inject(HdPlanetInfoService);
  private readonly fixedStarsPanel = inject(HdFixedStarsPanelService);
  private readonly hoverHighlight = inject(HdChartHoverHighlightService, { optional: true });
  private readonly ichingAccess = inject(HdIChingAccessService);
  private readonly ichingPanel = inject(HdIChingPanelService);
  private readonly chartSettings = inject(HdChartSettingsStore);

  /**
   * Geboortejaar (fractioneel) uit `birthData.time.utc`/`.local` — nodig voor
   * de precessie-correctie van de fixed stars. `null` wanneer de preset geen
   * bruikbaar geboortemoment draagt.
   */
  private readonly fixedStarBirthYear = computed<number | null>(() => {
    const birth = this.birthData() as {
      time?: { utc?: string | null; local?: string | null } | null;
    } | null;
    const iso = birth?.time?.utc || birth?.time?.local;
    if (!iso) {
      return null;
    }
    const parsed = moment.utc(iso);
    if (!parsed.isValid()) {
      return null;
    }
    return 2000 + parsed.diff(moment.utc('2000-01-01T00:00:00Z'), 'years', true);
  });

  /**
   * Extra fixed stars open (sessie-lokaal, standaard dicht — Steven
   * 2026-10-10): de archetypische laag is te ondergeschikt om standaard ruimte
   * in te nemen; enkel de HD fixed star (design-noordknoop) staat altijd in de
   * sectie. Pas open worden de extras ook berekend.
   */
  protected readonly fixedStarExtrasVisible = signal(false);

  protected toggleFixedStarExtras(): void {
    this.fixedStarExtrasVisible.update(visible => !visible);
  }

  /**
   * Fixed star-sectie (Geavanceerd-paneel): de 66 Sides-ster het dichtst bij de
   * design-noordknoop, en op aanvraag de extra sterren binnen 2° van elke
   * planeetactivatie. Labels volgen de content-taal via de
   * `human-design/fixed-stars`-scope.
   */
  protected readonly fixedStarsVm = computed<FixedStarsVm>(() => {
    const lang = this.contentLang.effectiveLang();
    const t = (key: string) => this.gateInfo.cheatSheetTextIn('fixedStars', key, lang)
      || this.gateInfo.cheatSheetTextIn('fixedStars', key, 'nl');
    const extrasVisible = this.fixedStarExtrasVisible();
    const base: Omit<FixedStarsVm, 'available' | 'primary' | 'extras'> = {
      title: t('properties.sectionTitle'),
      unknown: t('properties.unknown'),
      extrasTitle: t('properties.extrasTitle'),
      extrasEmpty: t('properties.extrasEmpty'),
      extrasToggle: t(extrasVisible ? 'properties.hideExtras' : 'properties.showExtras'),
      infoTooltip: t('properties.infoTooltip'),
      extrasInfoTooltip: t('properties.extrasInfoTooltip'),
      generalInfoTooltip: t('properties.generalInfoTooltip'),
    };

    const birthYear = this.fixedStarBirthYear();
    const activations = this.preset()?.planetaryActivations ?? [];
    if (birthYear == null || activations.length === 0) {
      return { ...base, available: false, primary: null, extras: [] };
    }

    const result = resolveHdFixedStars(activations, birthYear, { includeExtras: extrasVisible });
    const planetNames = this.planetInfo.planetTranslationsAllIn(lang);
    const sideLabel = (side: 'design' | 'personality') =>
      this.transloco.translate<string>(`humanDesign.bodygraphSide.${side}`, undefined, lang);

    const primary: FixedStarRowVm | null = result.primary
      ? {
          side: result.primary.star.side,
          name: result.primary.star.name,
          distance: formatHdFixedStarDistance(result.primary.distanceDegrees),
          label: `${t('properties.primaryLabel')} ${result.primary.gate}.${result.primary.line}`,
          planetGlyph: getHdPlanetDisplayConfig('northNode').astronomiconChar,
          context: {
            planetKey: 'northNode',
            activationSide: 'design',
            activationLongitude: result.primary.activationLongitude,
            correctionDegrees: result.precessionCorrectionDegrees,
            correctedLongitude: result.primary.correctedLongitude,
            distanceDegrees: result.primary.distanceDegrees,
            gate: result.primary.gate,
            line: result.primary.line,
          },
        }
      : null;

    const extras = result.extras.map<FixedStarRowVm>(extra => ({
      side: extra.star.side,
      name: extra.star.name,
      distance: formatHdFixedStarDistance(extra.distanceDegrees),
      label: `${sideLabel(extra.side)} ${planetNames[extra.planetKey]?.name ?? extra.planetKey} ${extra.gate}.${extra.line}`,
      planetGlyph: getHdPlanetDisplayConfig(extra.planetKey).astronomiconChar,
      context: {
        planetKey: extra.planetKey,
        activationSide: extra.side,
        activationLongitude: extra.activationLongitude,
        correctionDegrees: result.precessionCorrectionDegrees,
        correctedLongitude: extra.correctedLongitude,
        distanceDegrees: extra.distanceDegrees,
        gate: extra.gate,
        line: extra.line,
      },
    }));

    return { ...base, available: primary != null, primary, extras };
  });

  /** Opent het fixed-star-info-paneel voor een rij uit de Fixed star-sectie. */
  protected openFixedStarInfo(row: FixedStarRowVm, event: MouseEvent): void {
    if (this.guestBlocked(event)) return;
    this.fixedStarsPanel.openForStar(row.side, row.context, { x: event.clientX, y: event.clientY });
  }

  /** Opent de uitleg bij de "Extra fixed stars"-lijst. */
  protected openFixedStarExtrasInfo(event: MouseEvent): void {
    if (this.guestBlocked(event)) return;
    this.fixedStarsPanel.openExtrasInfo({ x: event.clientX, y: event.clientY });
  }

  /** Opent de algemene info-pagina over fixed stars en de 66 Sides. */
  protected openFixedStarGeneralInfo(event: MouseEvent): void {
    if (this.guestBlocked(event)) return;
    this.fixedStarsPanel.openGeneralInfo({ x: event.clientX, y: event.clientY });
  }

  /** Geboortegegevens zijn standaard gemaskeerd; pas na klik op "Toon" zichtbaar. */
  protected readonly birthDataVisible = signal(false);

  /** Gast: niets openen, wel melden. True = de aanroeper stopt. */
  private guestBlocked(event?: Event): boolean {
    if (this.viewMode() !== 'guest') return false;
    event?.preventDefault();
    event?.stopPropagation();
    this.accountRequired.emit();
    return true;
  }

  /** Uit de per-chart datacache (spec 2026-10-06): een tabwissel naar een bezochte chart doet geen call. */
  protected readonly relations = computed<PlatformChartRelationDto[]>(() =>
    this.dataCache.relations.value(this.savedChartId()) ?? []);
  /** Menselijke relaties: systeemkoppelingen (cycle/composite/moment) blijven buiten de lijst. */
  protected readonly humanRelations = computed(() =>
    this.relations().filter(relation => !isHdSystemRelationType(relation.relationType)),
  );

  /**
   * Lijstrijen in de kiesbare-typen-volgorde van de relatie-dialog. Eén rij per
   * persoon: wie meerdere relatietypen draagt staat op zijn hoogst-gerangschikte
   * type, met de overige typen als icoontjes achter de naam (dubbele rijen met
   * dubbele acties zouden als fouten ogen). Binnen een type alfabetisch op naam.
   */
  protected readonly relationRows = computed<RelationRowVm[]>(() => {
    const byPerson = new Map<string, PlatformChartRelationDto[]>();
    for (const relation of this.humanRelations()) {
      // Zonder other-id (hoort niet voor te komen) nooit samenvoegen.
      const key = relation.otherChartId ?? `relation:${relation.id}`;
      const list = byPerson.get(key) ?? [];
      list.push(relation);
      byPerson.set(key, list);
    }
    return [...byPerson.values()]
      .map(relations => {
        const sorted = [...relations].sort((a, b) =>
          hdRelationTypeOrder(a.relationType) - hdRelationTypeOrder(b.relationType));
        return {
          relation: sorted[0],
          extraTypes: sorted.slice(1)
            .map(relation => relation.relationType)
            .filter((type): type is string => !!type),
        };
      })
      .sort((a, b) =>
        hdRelationTypeOrder(a.relation.relationType) - hdRelationTypeOrder(b.relation.relationType)
        || (a.relation.otherChartName ?? '').localeCompare(b.relation.otherChartName ?? ''));
  });
  /** Enkel bij koud laden; een stille verversing laat de bestaande lijst staan. */
  protected readonly relationsLoading = computed(() => {
    const entry = this.dataCache.relations.entry(this.savedChartId());
    return !!entry?.loading && entry.value === undefined;
  });
  protected readonly relationsError = computed(() => {
    const entry = this.dataCache.relations.entry(this.savedChartId());
    return entry?.value === undefined ? (entry?.error ?? '') : '';
  });
  /** Target van de relatiebeheer-dialog (de "+"-knop); null = gesloten. */
  protected readonly relationsDialogTarget = signal<HdChartRelationsTarget | null>(null);

  constructor() {
    // Kruisnamen komen uit de crosses-scope; beide talen eager voor de NL-fallback.
    this.gateInfo.loadScopesForLang('nl');
    this.gateInfo.loadScopesForLang('en');
    effect(onCleanup => {
      const chartId = this.savedChartId();
      // Gedeelde chart: geen relaties-call (de sectie toont er toch niets van).
      if (chartId && this.viewMode() === 'owner') onCleanup(this.dataCache.relations.watch(chartId));
    });
    // Oudere cycle-documenten dragen soms alleen het anchor als passage; voor de
    // einddatum halen we de volledige kruisingen dan alsnog bij de bronchart op.
    effect(() => {
      const cycle = this.preset()?.cycle ?? null;
      // Gedeelde chart: de bronchart is van een andere eigenaar (geen call; gast heeft geen JWT).
      if (cycle == null || this.viewMode() !== 'owner' || !this.needsPassagesFallback(cycle)) {
        this.fallbackPassages.set(null);
        return;
      }
      const token = this.cycleToken(cycle);
      if (this.fallbackPassages()?.token === token) return;
      void this.loadFallbackPassages(cycle, token);
    });
  }

  /** Kruisingen-fallback voor de einddatum (zie constructor-effect); token bindt het resultaat aan de cyclus. */
  private readonly fallbackPassages = signal<{ token: string; passages: HdCyclePassageV1[] } | null>(null);

  /** Solar return en Just now leiden hun einde niet uit passages af; >1 passage in het document volstaat al. */
  private needsPassagesFallback(cycle: HdCycleChartInfoV1): boolean {
    return SHOW_CYCLE_END_DATE
      && cycle.key !== 'justNow'
      && cycle.key !== 'solarReturn'
      && (cycle.passages?.length ?? 0) <= 1
      && !!cycle.source.chartId;
  }

  private cycleToken(cycle: HdCycleChartInfoV1): string {
    return `${cycle.key}|${cycle.anchorUtc}|${cycle.source.chartId}`;
  }

  private async loadFallbackPassages(cycle: HdCycleChartInfoV1, token: string): Promise<void> {
    try {
      const dto = await this.cyclesService.getCycleMoments(cycle.source.chartId, cycle.solarReturnYear);
      const match = findHdCycleMoment(dto, cycle);
      const passages: HdCyclePassageV1[] = (match?.passages ?? [])
        .map(passage => ({ utc: passage.utc ?? '', retrograde: passage.retrograde ?? false }));
      // Chart kan intussen gewisseld zijn; verouderd resultaat negeren.
      const current = this.preset()?.cycle;
      if (!current || this.cycleToken(current) !== token) return;
      this.fallbackPassages.set({ token, passages });
    } catch {
      // Zonder fallback blijft de rij gewoon zonder einddatum staan.
    }
  }

  protected readonly text = computed(() => {
    const translate = this.contentTranslate();
    return {
      propertiesTitle: translate(`${P}.properties`),
      relationsTitle: translate('humanDesign.chartManager.relations.title'),
      noBirthData: translate('humanDesign.advancedPanel.noBirthData'),
      saveFirst: translate('humanDesign.advancedPanel.saveFirst'),
      sharedUnavailable: translate('humanDesign.advancedPanel.sharedUnavailable'),
      guestUnavailable: translate('humanDesign.advancedPanel.guestUnavailable'),
      addRelation: translate('humanDesign.chartManager.relations.add'),
      noRelations: translate('humanDesign.chartManager.relations.none'),
      loading: translate('humanDesign.chartManager.detail.loading'),
      showRelations: translate('humanDesign.advancedPanel.showRelations'),
      hideRelations: translate('humanDesign.advancedPanel.hideRelations'),
      compositeCalculate: translate('humanDesign.advancedPanel.composite.calculate'),
      compositeOpen: translate('humanDesign.advancedPanel.composite.open'),
      compositeCalculateTooltip: translate('humanDesign.advancedPanel.composite.calculateTooltip'),
      compositeOpenTooltip: translate('humanDesign.advancedPanel.composite.openTooltip'),
      generalInfo: translate('humanDesign.advancedPanel.composite.generalInfo'),
      ichingYourHexagram: translate('humanDesign.advancedPanel.classicIChing.yourHexagram'),
      ichingPair: translate('humanDesign.advancedPanel.classicIChing.pair'),
      ichingOpen: translate('humanDesign.advancedPanel.classicIChing.open'),
    };
  });

  /** Relaties-sectie is standaard dichtgeklapt; de chevron rechts klapt open/dicht. */
  protected readonly relationsExpanded = signal(false);

  protected toggleRelationsExpanded(): void {
    this.relationsExpanded.update(expanded => !expanded);
  }

  protected readonly birth = computed<BirthVm | null>(() => {
    this.contentLang.translationsLoadedTick();
    const lang = this.contentLang.effectiveLang();
    const translate = (key: string) => this.transloco.translate<string>(`${P}.${key}`, undefined, lang);
    return this.birthVm(this.birthData(), lang, translate);
  });

  /** Cycle chart ⇔ het preset draagt een cycle-blok. */
  protected readonly isCycleChart = computed(() => this.preset()?.cycle != null);

  /** Composite chart ⇔ het preset draagt een composite-blok. */
  protected readonly isCompositeChart = computed(() => this.preset()?.composite != null);

  // ── Classic I Ching (HexWave-ronde): alleen doorklikiconen, detail in het paneel ──

  /** Iconen zichtbaar: Classic I Ching aan (feature + setting) én de gebiedssetting voor de chart. */
  protected readonly ichingIconsVisible = computed(
    () => this.ichingAccess.classicVisible() && this.chartSettings.classicIChingIconsChart(),
  );

  /** "Jouw hexagram": bewuste en onbewuste zon plus de lokale geboortetijd; undefined zonder zon-activaties of bij een composite. */
  protected readonly ichingBirthTarget = computed<HdIChingBirthTarget | undefined>(() => {
    const preset = this.preset();
    if (!preset || this.isCompositeChart()) {
      return undefined;
    }
    const personality = findSunActivation(preset.planetaryActivations, 'personality');
    const design = findSunActivation(preset.planetaryActivations, 'design');
    if (!personality || !design) {
      return undefined;
    }
    const local = (this.birthData() as { time?: { local?: string | null } | null } | null)?.time?.local;
    const name = this.chartName();
    return {
      view: 'birth',
      ...(name ? { name } : {}),
      personalitySun: { gate: personality.gate, line: personality.line as IChingLinePosition },
      designSun: { gate: design.gate, line: design.line as IChingLinePosition },
      ...(local ? { birthLocal: local.slice(0, 16) } : {}),
    };
  });

  /** "Twee hexagrammen": de bewuste zon van beide personen van een composite. */
  protected readonly ichingPairTarget = computed<HdIChingPairTarget | undefined>(() => {
    const preset = this.preset();
    const composite = preset?.composite;
    if (!preset || !composite) {
      return undefined;
    }
    const a = findSunActivation(preset.planetaryActivations, 'personality');
    const b = findSunActivation(composite.partnerChart.planetaryActivations, 'personality');
    if (!a || !b) {
      return undefined;
    }
    return {
      view: 'pair',
      a: { name: composite.source.name, gate: a.gate, line: a.line as IChingLinePosition },
      b: { name: composite.partner.name, gate: b.gate, line: b.line as IChingLinePosition },
    };
  });

  protected openIChingBirth(event: MouseEvent): void {
    if (this.guestBlocked(event)) return;
    const target = this.ichingBirthTarget();
    if (target) {
      this.ichingPanel.openBirth(target, { x: event.clientX, y: event.clientY });
    }
  }

  protected openIChingPair(event: MouseEvent): void {
    if (this.guestBlocked(event)) return;
    const target = this.ichingPairTarget();
    if (target) {
      this.ichingPanel.openPair(target, { x: event.clientX, y: event.clientY });
    }
  }

  /** Transit-/snapshot-chart ⇔ het preset draagt een transit-blok. */
  protected readonly isTransitChart = computed(() => this.preset()?.transit != null);

  /**
   * Betrouwbaarheidschip van het geboortevenster: enkel standaardcharts met een
   * geboortemoment en planeetactivaties (afgeleide charts hebben geen eigen
   * geboortetijd om te verschuiven; besluit Steven 2026-10-16: chip bij de
   * geboortegegevens zodat gegevens en hun nauwkeurigheid samen staan).
   */
  protected readonly showBirthWindowIndicator = computed(() => {
    const preset = this.preset();
    const time = (this.birthData() as { time?: { utc?: string | null } | null } | null)?.time;
    return HD_BIRTH_WINDOW_CHART_TYPES.has(this.chartType())
      && !this.isCycleChart()
      && !this.isCompositeChart()
      && !this.isTransitChart()
      && (preset?.planetaryActivations?.length ?? 0) > 0
      && !!time?.utc;
  });

  /** Chart van een moment (Just Now, Rave New Year): de chip spreekt over het tijdstip, niet over de geboorte. */
  protected readonly birthWindowMoment = computed(() => this.chartType() === PlatformChartType.JustNow);

  /**
   * Momentvenster op een bewaard moment (Steven 2026-10-25): chip in de
   * momentrij. Het venster schuift het tijdstip van het moment, de natal chart
   * blijft als vaste laag. Niet voor een periode (geen vast tijdstip) en niet
   * voor gasten (geen geboortegegevens).
   */
  protected readonly momentBirthWindow = computed(() => {
    const preset = this.preset();
    if (preset == null || this.viewMode() === 'guest' || this.chartType() !== PlatformChartType.Transit) return null;
    const windowPreset = buildHdBirthWindowMomentPreset(preset);
    if (windowPreset == null) return null;
    return { preset: windowPreset, overlay: hdBirthWindowOverlayOf(preset, this.chartSettings.justNowTransitColorMode()) };
  });

  /**
   * Fixed stars horen enkel bij standaardcharts (Steven 2026-09-26): op
   * relaties/composites leidt de ster enkel uit één persoon af, en een cyclus-
   * of momentchart draagt de natal-snapshot top-level — daar zou de sectie de
   * sterren van de bronchart herhalen alsof ze bij het moment hoorden.
   */
  protected readonly showFixedStars = computed(() =>
    this.chartType() === PlatformChartType.Standard
    && !this.isCycleChart()
    && !this.isCompositeChart()
    && !this.isTransitChart(),
  );

  /**
   * "Moment"-rij + bronchart-link van een snapshot-chart (spec notable
   * moments §4.3); het moment is geen gevoelig gegeven en blijft ongemaskeerd.
   * Bij een periode (range-moment) toont de rij begin én einde.
   */
  protected readonly transitMomentInfo = computed(() => {
    const transit = this.preset()?.transit;
    if (!transit) return null;
    this.contentLang.translationsLoadedTick();
    const translate = this.contentTranslate();
    const start = moment.utc(transit.momentUtc).local().format('D MMMM YYYY, HH:mm');
    return {
      momentLabel: translate('humanDesign.advancedPanel.momentUtc'),
      momentText: transit.momentEndUtc
        ? `${start} – ${moment.utc(transit.momentEndUtc).local().format('D MMMM YYYY, HH:mm')}`
        : start,
      natalLabel: translate('humanDesign.advancedPanel.cycles.natal'),
      natalText: `${transit.source.name} — ${translate('humanDesign.advancedPanel.cycles.open')}`,
      chartId: transit.source.chartId,
    };
  });

  protected openTransitSource(): void {
    if (this.guestBlocked()) return;
    const info = this.transitMomentInfo();
    if (info?.chartId) this.chartOpenRequested.emit(info.chartId);
  }

  /** Natal-link van een cycle chart — rij in de Eigenschappen-sectie. */
  protected readonly natalLink = computed(() => {
    const cycle = this.preset()?.cycle;
    if (!cycle) return null;
    const translate = this.contentTranslate();
    return {
      label: translate('humanDesign.advancedPanel.cycles.natal'),
      text: `${cycle.source.name} — ${translate('humanDesign.advancedPanel.cycles.open')}`,
      chartId: cycle.source.chartId,
    };
  });

  /**
   * Eigenschappen van een composite chart: per persoon de geboortegegevens
   * (zelfde maskering als de gewone rij) en de natal-link mét type · profiel.
   */
  protected readonly compositePersons = computed(() => {
    const preset = this.preset();
    const composite = preset?.composite;
    if (!preset || !composite) return null;
    this.contentLang.translationsLoadedTick();
    const lang = this.contentLang.effectiveLang();
    const translate = this.contentTranslate();
    const t = (key: string) => this.transloco.translate<string>(`${P}.${key}`, undefined, lang);
    const person = (ref: HdCompositeSourceRefV1, birthData: Record<string, unknown> | null) => {
      const meta = [hdTypeLabel(translate, ref.hdType), ref.hdProfile]
        .filter((part): part is string => !!part).join(' · ');
      return {
        name: ref.name,
        birth: this.birthVm(birthData, lang, t),
        natalLabel: translate('humanDesign.advancedPanel.cycles.natal'),
        natalText: `${ref.name}${meta ? ` — ${meta}` : ''} — ${translate('humanDesign.advancedPanel.cycles.open')}`,
        chartId: ref.chartId,
      };
    };
    return [
      person(composite.source, preset.birthData ?? null),
      person(composite.partner, composite.partnerChart.birthData ?? null),
    ];
  });

  /**
   * Boek-eigenschappen van een composite chart: type, profiel en beide kruisen
   * per persoon (in de vaste persoon-kleuren), elk met een info-icoon naar het
   * bijbehorende relatieboek-onderwerp. Kruisen worden uit de eigen
   * planetaire activaties van elke chart afgeleid (partnerChart draagt geen
   * propertiesSummary).
   */
  protected readonly compositeInfoRows = computed<CompositeInfoRowVm[]>(() => {
    const preset = this.preset();
    const composite = preset?.composite;
    if (!preset || !composite) return [];
    this.contentLang.translationsLoadedTick();
    const lang = this.contentLang.effectiveLang();
    const translate = this.contentTranslate();
    const t = (key: string) => this.transloco.translate<string>(`${P}.${key}`, undefined, lang);
    const infoLabel = translate('humanDesign.advancedPanel.composite.relationInfo');

    const refs = [composite.source, composite.partner];
    const personActivations = [
      preset.planetaryActivations ?? [],
      composite.partnerChart.planetaryActivations ?? [],
    ];
    const personColors = [HD_JUST_NOW_TRANSIT_BASE_COLOR, HD_JUST_NOW_TRANSIT_TEXT_COLOR];
    const asValues = (texts: readonly (string | null)[]) =>
      texts.map((text, index) => ({ text: text || '—', color: personColors[index] }));

    return [
      {
        topic: 'type',
        label: t('type'),
        infoLabel,
        layout: 'inline',
        values: asValues(refs.map(ref => hdTypeLabel(translate, ref.hdType))),
      },
      {
        topic: 'profile',
        label: t('profile'),
        infoLabel,
        layout: 'inline',
        values: asValues(refs.map(ref => ref.hdProfile ?? null)),
      },
      {
        topic: 'nodes',
        label: t('incarnationCross'),
        infoLabel,
        layout: 'stacked',
        values: asValues(personActivations.map(activations =>
          formatHdIncarnationCrossValue(deriveIncarnationCross(activations), t, lang, this.gateInfo))),
      },
      {
        topic: 'nodes',
        label: t('lifeCross'),
        infoLabel,
        layout: 'stacked',
        values: asValues(personActivations.map(activations =>
          formatHdLifeCrossValue(deriveLifeCross(activations)))),
      },
    ];
  });

  protected requestRelationInfo(topic: HdRelationInfoTopic, event: MouseEvent): void {
    if (this.guestBlocked(event)) return;
    this.relationInfoRequested.emit({
      topic,
      origin: { x: event.clientX, y: event.clientY },
    });
  }

  /**
   * Locks & Keys slot-sleutelrelatie tussen de twee personen van de composite:
   * welke sleutel van de ene chart een slot van de andere opent, voor de nú
   * geldende periode (vóór/vanaf de 2027-shift, op datum). De volledige
   * weergave met pre/post-toggle leeft in de L&K-modus (knop hieronder).
   */
  /**
   * Periode van de slot-sleutelrelatie-sectie: default de nú geldende periode
   * (vóór/vanaf de 2027-shift, op datum via {@link hdLocksKeysPeriodForDate}),
   * togglebaar naar de andere.
   */
  protected readonly relationshipPeriod = signal<HdLocksKeysPeriodId>(hdLocksKeysPeriodForDate());

  protected setRelationshipPeriod(period: HdLocksKeysPeriodId): void {
    this.relationshipPeriod.set(period);
  }

  protected readonly locksKeysRelationship = computed<LocksKeysRelationshipVm | null>(() => {
    const preset = this.preset();
    const composite = preset?.composite;
    if (!preset || !composite) return null;
    this.contentLang.translationsLoadedTick();
    const lang = this.contentLang.effectiveLang();
    const t = (key: string) => this.gateInfo.locksKeysTextIn(key, lang);
    const period = this.relationshipPeriod();
    const rows = buildHdLocksKeysRelationshipRows(
      this.gateInfo,
      lang,
      period,
      { name: composite.source.name, color: HD_JUST_NOW_TRANSIT_BASE_COLOR, activations: preset.planetaryActivations ?? [] },
      { name: composite.partner.name, color: HD_JUST_NOW_TRANSIT_TEXT_COLOR, activations: composite.partnerChart.planetaryActivations ?? [] },
    );
    const periodOptions = ([
      { id: 'planning', key: 'panel.relPeriodPre' },
      { id: 'sleepingPhoenix', key: 'panel.relPeriodPost' },
    ] as const).map<ESegmentedToggleOption<HdLocksKeysPeriodId>>(option => ({
      value: option.id,
      label: t(option.key),
    }));
    return {
      title: t('panel.relTitle'),
      hint: t('panel.relHint'),
      periodLabel: t('panel.relPeriodLabel'),
      periodOptions,
      rows,
      none: t('panel.relNone'),
      moreInfo: t('panel.relMoreInfo'),
    };
  });

  protected requestLocksKeys(): void {
    if (this.guestBlocked()) return;
    this.locksKeysRequested.emit();
  }

  private readonly lovePanel = inject(HdLovePanelService);

  /**
   * Connectiviteitspoorten (alleen composite): de liefdespoorten
   * ({@link HD_LOVE_GATES}) die minstens één van beide charts activeert, met de
   * basic love-keynote (de titel op de Love gates cheat sheet) en per poort wie
   * hem activeert (chart 1 blauw / chart 2 groen, zoals overal). Klik opent het
   * love-gates-paneel, gescrolld naar die poort.
   */
  protected readonly loveConnectivity = computed<LoveConnectivityVm | null>(() => {
    const preset = this.preset();
    const composite = preset?.composite;
    if (!preset || !composite) return null;
    this.contentLang.translationsLoadedTick();
    const lang = this.contentLang.effectiveLang();
    const chart1Gates = getHdActivatedGates(preset.planetaryActivations ?? []);
    const chart2Gates = getHdActivatedGates(composite.partnerChart.planetaryActivations ?? []);
    const rows = HD_LOVE_GATES
      .filter(gate => chart1Gates.has(gate) || chart2Gates.has(gate))
      .map<LoveConnectivityRowVm>(gate => ({
        gate,
        keynote: this.loveText(`gates.${gate}.keynote`, lang),
        persons: [
          ...(chart1Gates.has(gate)
            ? [{ name: composite.source.name, color: HD_JUST_NOW_TRANSIT_BASE_COLOR }]
            : []),
          ...(chart2Gates.has(gate)
            ? [{ name: composite.partner.name, color: HD_JUST_NOW_TRANSIT_TEXT_COLOR }]
            : []),
        ],
      }));
    return {
      title: this.loveText('connectivity.title', lang),
      hint: this.loveText('connectivity.hint', lang),
      rows,
      none: this.loveText('connectivity.none', lang),
    };
  });

  protected openLoveGate(gate: number, event: MouseEvent): void {
    if (this.guestBlocked(event)) return;
    this.lovePanel.openForGate(gate, { x: event.clientX, y: event.clientY });
  }

  /** Love-scope-tekst met NL-fallback voor (nog) lege EN-velden. */
  private loveText(key: string, lang: string): string {
    const value = this.gateInfo.cheatSheetTextIn('love', key, lang);
    if (value !== '' || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cheatSheetTextIn('love', key, 'nl');
  }

  /** "Composiet-definitie" van de volledige combinatie (single/split/triple/quadruple). */
  protected readonly compositeDefinition = computed(() => {
    const preset = this.preset();
    if (!preset || preset.composite == null) return null;
    this.contentLang.translationsLoadedTick();
    const lang = this.contentLang.effectiveLang();
    const definitionKey = deriveDefinition(this.renderStateFactory.create(mergeCycleConfiguration(preset)));
    return {
      label: this.transloco.translate<string>('humanDesign.advancedPanel.composite.definitionLabel', undefined, lang),
      text: this.transloco.translate<string>(`${P}.definitionValues.${definitionKey}`, undefined, lang),
    };
  });

  /**
   * Primaire conditionering van de volledige composiet-combinatie: bij een
   * single/triple-split-definitie de open centers (niet-zelf-volgorde), bij
   * een (quadruple) split de mogelijke brugpoorten tussen de
   * definitie-eilanden. Bewust zónder de zon/aarde-prioriteit van het
   * Basis-paneel (advanced=false): die rangschikking hoort bij één persoon en
   * zou hier willekeurig chart 1 bevoordelen — brugpoorten sorteren op
   * poortnummer.
   */
  protected readonly compositeConditioning = computed<HdPrimaryConditioningVm | null>(() => {
    const preset = this.preset();
    if (!preset || preset.composite == null) return null;
    this.contentLang.translationsLoadedTick();
    const lang = this.contentLang.effectiveLang();
    const t = (key: string) => this.transloco.translate<string>(`${P}.${key}`, undefined, lang);
    const state = this.renderStateFactory.create(mergeCycleConfiguration(preset));
    const conditioning = derivePrimaryConditioning(state, mergeCyclePlanetaryActivations(preset), false);
    if (conditioning.kind === 'none') return null;
    return buildHdPrimaryConditioningVm(conditioning, t, center => this.centerColors.color(center), this.bridgeGateStyle());
  });

  /** Hover op een brugpoort → highlight de poort in de chart/planeetgrid. */
  protected onBridgeGateEnter(gate: number): void {
    this.hoverHighlight?.setGateHover(gate, 'chart-gate');
  }

  protected onBridgeGateLeave(): void {
    this.hoverHighlight?.setGateHover(null, 'chart-gate');
  }

  /** Klik op een brugpoort → opent het gate-info paneel (zoals een klik in de chart). */
  protected onBridgeGateClick(gate: number, event: MouseEvent): void {
    if (this.guestBlocked(event)) return;
    this.gateInfoRequested.emit({ gate, origin: { x: event.clientX, y: event.clientY } });
  }

  /** Hover op een center-naam → highlight het center in de chart. */
  protected onConditioningCenterEnter(center: HdBodygraphCenter): void {
    this.hoverHighlight?.setCenterHover(center);
  }

  protected onConditioningCenterLeave(): void {
    this.hoverHighlight?.setCenterHover(null);
  }

  /** Info-icoon op de conditioneringsrij → conditionering-info-paneel. */
  protected onConditioningInfoClick(event: MouseEvent): void {
    if (this.guestBlocked(event)) return;
    this.conditioningInfoRequested.emit({ origin: { x: event.clientX, y: event.clientY } });
  }

  /**
   * Composiet-thema op basis van het aantal gedefinieerde centers in de
   * combinatie: 9→"nowhere to go" … 5→"not a relationship any more"; minder
   * dan 5 gedefinieerd = expliciet "geen thema". Teksten uit de cycles-scope.
   */
  protected readonly compositeTheme = computed(() => {
    const preset = this.preset();
    if (!preset || preset.composite == null) return null;
    this.contentLang.translationsLoadedTick();
    const lang = this.contentLang.effectiveLang();
    const state = this.renderStateFactory.create(mergeCycleConfiguration(preset));
    const definedCount = Object.values(state.centerStates)
      .filter(center => center.isDefined).length;
    const themeKey = definedCount >= 5 ? `${definedCount}` : 'none';
    return {
      key: themeKey,
      label: this.cyclesText('composite.themeLabel', lang),
      text: this.cyclesText(`composite.themes.${themeKey}`, lang),
      infoLabel: this.transloco.translate<string>('humanDesign.advancedPanel.composite.themeInfo', undefined, lang),
    };
  });

  protected requestCompositeThemeInfo(event: MouseEvent): void {
    if (this.guestBlocked(event)) return;
    const theme = this.compositeTheme();
    if (theme == null) return;
    this.compositeThemeInfoRequested.emit({
      theme: theme.text,
      themeKey: theme.key,
      origin: { x: event.clientX, y: event.clientY },
    });
  }

  /** Tekst uit de cycles-scope met NL-fallback voor (nog) lege EN-velden. */
  private cyclesText(key: string, lang: string): string {
    const value = this.gateInfo.cyclesCheatSheetTextIn(key, lang);
    if (value !== '' || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cyclesCheatSheetTextIn(key, 'nl');
  }

  protected openCompositePerson(chartId: string | null): void {
    if (this.guestBlocked()) return;
    if (chartId) this.chartOpenRequested.emit(chartId);
  }

  /**
   * Berekenen/Openen-actie op een relatie-rij: bestaat er al een opgeslagen
   * composite van de geladen chart + deze persoon (herkend via de
   * hasComposite-relaties en hun bron-ids), dan "Openen"; anders "Berekenen".
   * Alleen op een opgeslagen Standard-chart.
   */
  protected compositeActionFor(
    relation: PlatformChartRelationDto,
  ): { kind: 'open'; compositeId: string } | { kind: 'calculate'; partnerChartId: string } | null {
    const ownId = this.savedChartId();
    const partnerId = relation.otherChartId;
    if (!ownId || !partnerId || this.chartType() !== PlatformChartType.Standard) return null;
    const existing = this.relations().find(candidate =>
      candidate.relationType === 'hasComposite'
      && candidate.otherChartId != null
      && [candidate.otherChartCompositeSourceChartId1, candidate.otherChartCompositeSourceChartId2]
        .filter((id): id is string => id != null)
        .sort()
        .join('|') === [ownId, partnerId].sort().join('|'));
    return existing?.otherChartId
      ? { kind: 'open', compositeId: existing.otherChartId }
      : { kind: 'calculate', partnerChartId: partnerId };
  }

  protected onCompositeAction(relation: PlatformChartRelationDto): void {
    if (this.guestBlocked()) return;
    const action = this.compositeActionFor(relation);
    if (action == null) return;
    if (action.kind === 'open') {
      this.chartOpenRequested.emit(action.compositeId);
    } else {
      this.compositeCalculateRequested.emit({ partnerChartId: action.partnerChartId });
    }
  }

  protected openNatal(): void {
    if (this.guestBlocked()) return;
    const link = this.natalLink();
    if (link?.chartId) this.chartOpenRequested.emit(link.chartId);
  }

  /**
   * "Cycle tijdstip"-rij onder de geboortegegevens (alleen op een cycle chart);
   * bewust niet gemaskeerd — het cyclusmoment is geen gevoelig gegeven. De
   * tooltip draagt dezelfde tijdstip-details als de geboortedatum (tijdzone,
   * offset, coördinaten) uit de birthData van het cycle-moment.
   */
  protected readonly cycleMoment = computed(() => {
    const cycle = this.preset()?.cycle;
    if (!cycle) return null;
    this.contentLang.translationsLoadedTick();
    const lang = this.contentLang.effectiveLang();
    const momentBirth = (cycle.moment.birthData ?? {}) as HdPresetBirthData;
    // Leeftijd van de natal-persoon (de geboortegegevens van deze chart) op het cyclusmoment.
    const natalTime = (this.birthData() as HdPresetBirthData | null)?.time ?? null;
    return {
      label: this.contentTranslate()('humanDesign.advancedPanel.cycleMoment'),
      text: moment.utc(cycle.anchorUtc).local().format('D MMMM YYYY, HH:mm'),
      age: this.ageYears(natalTime, this.parseBirthDateTime(natalTime), moment.utc(cycle.anchorUtc)),
      endText: SHOW_CYCLE_END_DATE ? this.cycleEndText(cycle, lang) : null,
      tooltip: this.birthTooltip(momentBirth.time ?? null, momentBirth.location ?? null, lang),
      design: this.cycleDesignVm(momentBirth.time ?? null, lang),
    };
  });

  /**
   * "Design tijdstip"-rij onder het cycle-tijdstip: het 88°-moment van de
   * cyclus-chart, net als het cyclusmoment zelf onverborgen en in dezelfde
   * (browser-lokale) weergave als de rij erboven. Oudere documenten zonder
   * design-veld tonen geen rij.
   */
  private cycleDesignVm(time: HdPresetBirthTime | null, lang: string): HdDesignTimeVm | null {
    const iso = time?.design ?? null;
    const instant = iso ? moment.utc(iso) : null;
    if (instant == null || !instant.isValid()) return null;
    const translate = (key: string) => this.transloco.translate<string>(`${P}.${key}`, undefined, lang);
    return {
      label: translate('designTime'),
      text: instant.local().format('D MMMM YYYY, HH:mm'),
      tooltip: translate('designTimeTooltip'),
    };
  }

  /**
   * Einddatum van de cyclusperiode achter het tijdstip: de laatste kruising
   * (retrograde-heractuaties) wanneer die ná het anchor ligt; een solar
   * return geldt tot de volgende return (anchor plus een jaar). Just now en
   * cycli met één enkele kruising hebben geen aparte einddatum. Documenten
   * die alleen het anchor dragen vallen terug op de vers opgehaalde
   * kruisingen van de bronchart (zie {@link fallbackPassages}).
   */
  private cycleEndText(cycle: HdCycleChartInfoV1, lang: string): string | null {
    if (cycle.key === 'justNow') return null;
    let end: moment.Moment | null;
    const anchor = moment.utc(cycle.anchorUtc);
    if (cycle.key === 'solarReturn') {
      end = anchor.clone().add(1, 'year');
    } else {
      const sorted = this.effectivePassages(cycle)
        .map(passage => moment.utc(passage.utc))
        .filter(passage => passage.isValid())
        .sort((a, b) => a.valueOf() - b.valueOf());
      const first = sorted[0] ?? null;
      const last = sorted[sorted.length - 1] ?? null;
      // Sanity: de reeks moet bij dít anchor horen (een verouderde natal kan
      // fallback-kruisingen van een verschoven cyclus opleveren).
      const belongsToAnchor = first != null && Math.abs(first.diff(anchor, 'days')) <= 366;
      end = belongsToAnchor && last != null && last.isAfter(anchor) ? last : null;
    }
    if (end == null || !end.isValid()) return null;
    return this.transloco.translate<string>(
      'humanDesign.advancedPanel.cycleMomentUntil',
      { date: end.local().format('D MMMM YYYY') },
      lang,
    );
  }

  /** Kruisingen uit het document zelf; magere documenten (≤1 passage) gebruiken de opgehaalde fallback. */
  private effectivePassages(cycle: HdCycleChartInfoV1): readonly HdCyclePassageV1[] {
    const own = cycle.passages ?? [];
    if (own.length > 1) return own;
    const fallback = this.fallbackPassages();
    if (fallback != null && fallback.token === this.cycleToken(cycle)) return fallback.passages;
    return own;
  }

  protected relationIcon(relationType: string | null | undefined): string {
    return hdRelationTypeIcon(relationType);
  }

  protected relationLabel(relationType: string | null | undefined): string {
    return hdRelationTypeLabel(this.contentTranslate(), relationType);
  }

  /** Compacte "Type · profiel"-toevoeging achter de naam; '' wanneer onbekend. */
  protected relationMeta(relation: PlatformChartRelationDto): string {
    return [
      hdTypeLabel(this.contentTranslate(), relation.otherChartHdType),
      relation.otherChartHdProfile || null,
    ].filter((part): part is string => !!part).join(' · ');
  }

  protected openRelation(relation: PlatformChartRelationDto): void {
    if (this.guestBlocked()) return;
    if (relation.otherChartId) this.chartOpenRequested.emit(relation.otherChartId);
  }

  protected refreshRelations(): void {
    const chartId = this.savedChartId();
    if (chartId) void this.dataCache.relations.refresh(chartId);
  }

  /** "+"-knop: relatiebeheer-dialog direct voor de actieve chart openen. */
  protected openRelationsDialog(): void {
    if (this.guestBlocked()) return;
    const chartId = this.savedChartId();
    if (!chartId) return;
    this.relationsDialogTarget.set({ chartId, chartName: this.chartName() });
  }


  private contentTranslate(): (key: string) => string {
    this.contentLang.translationsLoadedTick();
    const lang = this.contentLang.effectiveLang();
    return (key: string) => this.transloco.translate<string>(key, undefined, lang);
  }

  /**
   * Geboortegegevens als één rij (verhuisd uit HdBasicPropertiesComponent). De
   * waarde is standaard gemaskeerd en wordt pas na een klik op de toon-knop
   * getoond; ontbrekende velden (oudere/geïmporteerde presets) vallen weg.
   */
  private birthVm(
    raw: Record<string, unknown> | null,
    lang: string,
    t: (k: string) => string,
  ): BirthVm | null {
    if (!raw) return null;
    const birth = raw as HdPresetBirthData;
    const time = birth.time ?? null;
    const location = birth.location ?? null;
    const parts = this.parseBirthDateTime(time);

    const values: string[] = [];
    if (parts) {
      values.push(new Intl.DateTimeFormat(lang, {
        day: 'numeric', month: 'long', year: 'numeric',
      }).format(parts.date));
      values.push(parts.timeText);
    }
    if (location?.city) values.push(location.city);
    if (location?.country) values.push(location.country);
    if (!values.length) return null;

    const designText = this.designTimeText(time, lang);
    return {
      label: t('birthData'),
      text: values.join(', '),
      age: this.ageYears(time, parts),
      tooltip: this.birthTooltip(time, location, lang),
      showLabel: t('birthShow'),
      hideLabel: t('birthHide'),
      design: designText
        ? { label: t('designTime'), text: designText, tooltip: t('designTimeTooltip') }
        : null,
    };
  }

  /**
   * Design-tijdstip in de geboortetijdzone (zelfde referentiekader als de
   * geboorterij erboven); Intl regelt daarbij de DST-stand op het
   * design-moment zelf. Zonder bruikbare IANA-id valt de weergave terug op
   * UTC, expliciet gelabeld.
   */
  private designTimeText(time: HdPresetBirthTime | null, lang: string): string | null {
    const iso = time?.design ?? null;
    const instant = iso ? moment.utc(iso) : null;
    if (instant == null || !instant.isValid()) return null;
    const date = instant.toDate();
    const timezoneId = time?.timezone?.id;
    if (timezoneId) {
      try {
        return this.formatInstantInZone(date, lang, timezoneId);
      } catch {
        // Onbekende/ongeldige zone-id (oudere documenten) → UTC-fallback.
      }
    }
    return `${this.formatInstantInZone(date, lang, 'UTC')} UTC`;
  }

  /** "23 september 1982, 03:21" in de gegeven IANA-zone. */
  private formatInstantInZone(date: Date, lang: string, timeZone: string): string {
    const datePart = new Intl.DateTimeFormat(lang, {
      day: 'numeric', month: 'long', year: 'numeric', timeZone,
    }).format(date);
    const timePart = new Intl.DateTimeFormat(lang, {
      hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone,
    }).format(date);
    return `${datePart}, ${timePart}`;
  }

  /**
   * Leeftijd in volle jaren op `at` (standaard nu; het cycle-tijdstip geeft
   * het cyclusmoment mee). Voorkeur voor het exacte UTC-moment; anders de
   * wandklokdatum. Een geboortemoment ná `at` levert geen leeftijd op; de
   * weergave zelf doet het gedeelde {@link HdAgeSuffixComponent}.
   */
  private ageYears(
    time: HdPresetBirthTime | null,
    parts: { date: Date } | null,
    at: moment.Moment = moment(),
  ): number | null {
    const birthInstant = time?.utc ? moment.utc(time.utc) : parts ? moment(parts.date) : null;
    if (birthInstant == null || !birthInstant.isValid() || !at.isValid()) return null;
    const years = at.diff(birthInstant, 'years');
    return years >= 0 ? years : null;
  }

  /**
   * Parset `time.local` (valt terug op `time.utc`) als platte ISO-string —
   * bewust géén Date-parsing van de volledige string, zodat de lokale tijd
   * niet verschuift door de browser-tijdzone.
   */
  private parseBirthDateTime(time: HdPresetBirthTime | null): { date: Date; timeText: string } | null {
    const iso = time?.local ?? time?.utc ?? null;
    if (!iso) return null;
    const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(iso);
    if (!match) return null;
    const [, year, month, day, hours, minutes] = match;
    return {
      date: new Date(Number(year), Number(month) - 1, Number(day)),
      timeText: `${hours}:${minutes}`,
    };
  }

  private birthTooltip(
    time: HdPresetBirthTime | null,
    location: HdPresetBirthLocation | null,
    lang: string,
  ): string | null {
    const translateLine = (key: string, params: Record<string, unknown>) =>
      this.transloco.translate(`${P}.birthTooltip.${key}`, params, lang);

    const lines: string[] = [];
    const timezone = time?.timezone ?? null;
    if (timezone?.id) {
      lines.push(translateLine('timezone', { timezone: timezone.id }));
    }
    if (timezone?.name) {
      lines.push(translateLine('timezoneName', { name: timezone.name }));
    }
    if (timezone?.offset != null) {
      const offset = `UTC${timezone.offset >= 0 ? '+' : ''}${timezone.offset}`;
      lines.push(translateLine('utcOffset', { offset }));
    }
    const latitude = location?.lat ?? location?.latitude ?? null;
    const longitude = location?.lon ?? location?.longitude ?? null;
    if (latitude != null && longitude != null) {
      lines.push(translateLine('coordinates', {
        latitude: latitude.toFixed(4),
        longitude: longitude.toFixed(4),
      }));
    }
    return lines.length ? lines.join('\n') : null;
  }
}

/** Zon-activatie (planetId 0) van de gevraagde zijde, of undefined. */
function findSunActivation(
  activations: HdBodygraphChartPreset['planetaryActivations'],
  side: 'personality' | 'design',
): HdBodygraphChartPreset['planetaryActivations'][number] | undefined {
  return activations.find(activation => activation.planetId === 0 && activation.side === side);
}
